import { useCallback, useEffect, useState } from 'react';

import { api, completeAutomaticReauthentication, type AppSummary, type User, type VibeCheckProposal } from './api';
import { c, font } from './tokens';
import { Sidebar } from './components/Sidebar';
import { SignIn } from './screens/SignIn';
import { Skills } from './screens/Skills';
import { Catalog } from './screens/Catalog';
import { VibeCheckSession } from './components/VibeCheckSession';
import { CreateWizard } from './screens/CreateWizard';
import { AppDetail, type AppDetailTab } from './screens/AppDetail';
import { Placeholder } from './screens/Placeholder';
import { StartHere } from './screens/StartHere';
import { Feedback } from './screens/Feedback';
import type { SessionState as LiveSessionState } from './components/LiveSessionTerminal';
import { clearCatalogFilters } from './catalogFilters';

export type Screen =
  | 'about'
  | 'catalog'
  | 'skills'
  | 'templates'
  | 'vibe-check'
  | 'wizard'
  | 'detail'
  | 'docs'
  | 'settings'
  | 'feedback';

const RETAINED_LIVE_SESSION_STATES = new Set<LiveSessionState>([
  'connecting',
  'connected',
  'stopping',
]);

const APP_DETAIL_TAB_BY_ID: Record<string, AppDetailTab> = {
  overview: 'Overview',
  'live-session': 'Live Session',
  deployments: 'Deployments',
  secrets: 'Secrets',
  docs: 'Docs',
  settings: 'Settings',
};

const APP_DETAIL_TAB_ID_BY_TAB: Record<AppDetailTab, string> = Object.fromEntries(
  Object.entries(APP_DETAIL_TAB_BY_ID).map(([id, tab]) => [tab, id]),
) as Record<AppDetailTab, string>;

/**
 * Hash routing. Deliberately dependency-free: there is a small fixed set of destinations and
 * the deep link that matters is `#/apps/<repo>` so a "your infra PR merged"
 * notification can point straight at an app.
 */
function parseHash(): { screen: Screen; repo?: string; templateId?: string; detailTab?: AppDetailTab } {
  if (!window.location.hash) return { screen: 'about' };
  const hash = window.location.hash.replace(/^#\/?/, '');
  const [path, query = ''] = hash.split('?', 2);
  const [head, tail] = [path.split('/')[0] ?? '', path.split('/').slice(1).join('/')];

  switch (head) {
    case 'start':
    case 'about':
      return { screen: 'about' };
    case 'apps':
      return tail
        ? {
            screen: 'detail',
            repo: tail,
            detailTab: APP_DETAIL_TAB_BY_ID[new URLSearchParams(query).get('tab') ?? ''],
          }
        : { screen: 'catalog' };
    case 'skills':
      return { screen: 'skills' };
    case 'vibe-check':
      return { screen: 'vibe-check' };
    case 'create':
      return { screen: 'wizard', templateId: tail || 'cdp-primary-app' };
    case 'docs':
      return { screen: 'docs' };
    case 'settings':
      return { screen: 'settings' };
    case 'feedback':
      return { screen: 'feedback' };
    case '':
      return { screen: 'catalog' };
    default:
      return { screen: 'about' };
  }
}

export function App() {
  const [route, setRoute] = useState(parseHash);
  const [wizardRevision, setWizardRevision] = useState(0);
  const [vibeProposal, setVibeProposal] = useState<VibeCheckProposal | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [apps, setApps] = useState<AppSummary[]>([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [appsError, setAppsError] = useState<string | null>(null);
  const [liveSessionStates, setLiveSessionStates] = useState<Record<string, LiveSessionState>>({});

  useEffect(() => {
    if (!authChecked) { document.title = 'Vibes · Abridge'; return; }
    if (!user) { document.title = 'Sign in · Vibes'; return; }
    const labels: Record<Screen, string> = {
      about: 'Start Here', catalog: 'Apps', skills: 'Skills', templates: 'Create an app',
      'vibe-check': 'Vibe Check', wizard: 'Create an app', detail: 'App',
      docs: 'Docs', settings: 'Settings', feedback: 'Feedback',
    };
    if (route.screen === 'detail') {
      const appName = apps.find(app => app.repo === route.repo)?.title || route.repo || 'App';
      document.title = `${appName} · ${route.detailTab ?? 'Overview'} · Vibes`;
    } else {
      document.title = `${labels[route.screen]} · Vibes`;
    }
  }, [authChecked, user, route.screen, route.repo, route.detailTab, apps]);

  useEffect(() => {
    const onHashChange = () => setRoute(parseHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    api
      .me()
      .then(({ user: me }) => {
        if (me) {
          completeAutomaticReauthentication();
        } else {
          clearCatalogFilters();
        }
        setUser(me);
      })
      .catch(() => {
        clearCatalogFilters();
        setUser(null);
      })
      .finally(() => setAuthChecked(true));
  }, []);

  const refreshApps = useCallback(() => {
    if (!user) return;
    setAppsLoading(true);
    setAppsError(null);
    api
      .apps()
      .then(({ apps: list }) => setApps(list))
      .catch((error: unknown) =>
        setAppsError(error instanceof Error ? error.message : 'Could not load your apps.'),
      )
      .finally(() => setAppsLoading(false));
  }, [user]);

  useEffect(refreshApps, [refreshApps]);

  const navigate = useCallback((screen: Screen, arg?: string) => {
    if (screen === 'templates') { setVibeProposal(null); setWizardRevision(value => value + 1); }
    const path =
      screen === 'catalog'
        ? '#/'
        : screen === 'templates'
          ? '#/create'
          : screen === 'wizard'
            ? `#/create/${arg ?? ''}`
            : screen === 'detail'
              ? `#/apps/${arg ?? ''}`
              : `#/${screen}`;
    window.location.hash = path;
  }, []);

  const navigateDetailTab = useCallback((repo: string, tab: AppDetailTab) => {
    const tabQuery = tab === 'Overview' ? '' : `?tab=${APP_DETAIL_TAB_ID_BY_TAB[tab]}`;
    window.location.hash = `#/apps/${repo}${tabQuery}`;
  }, []);

  const signOut = useCallback(() => {
    api
      .logout()
      .catch(() => undefined)
      .finally(() => {
        clearCatalogFilters();
        setUser(null);
        setVibeProposal(null);
        setApps([]);
        setLiveSessionStates({});
      });
  }, []);

  if (!authChecked) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: c.canvas,
          fontFamily: font,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: c.textMuted,
          fontSize: 14,
        }}
      >
        Loading…
      </div>
    );
  }

  if (!user) return <SignIn />;

  const pendingCount = apps.filter(
    app => !app.archived && app.status === 'pending-platform-review',
  ).length;
  const visibleDetailRepo = route.screen === 'detail' ? route.repo : undefined;
  const retainedDetailRepos = Object.entries(liveSessionStates)
    .filter(([, state]) => RETAINED_LIVE_SESSION_STATES.has(state))
    .map(([repo]) => repo);
  const mountedDetailRepos = visibleDetailRepo
    ? [visibleDetailRepo, ...retainedDetailRepos.filter(repo => repo !== visibleDetailRepo)]
    : retainedDetailRepos;

  return (
    <div
      className="vibes-shell"
      style={{
        display: 'flex',
        minHeight: '100vh',
        background: c.canvas,
        fontFamily: font,
        color: c.text,
      }}
    >
      <Sidebar
        screen={route.screen}
        user={user}
        onNavigate={navigate}
        onSignOut={signOut}
      />

      <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {route.screen === 'about' && <StartHere onNavigate={navigate} />}

        {route.screen === 'skills' && (user.isCdpAdmin === true || user.isSkillsPilot === true
          ? <Skills key={user.githubId} />
          : <section style={{ padding: 40 }}><h1>Skills is in early access.</h1><p>This section is available only to members of the skills-pilot or cdp-admins GitHub team.</p></section>)}

        {route.screen === 'catalog' && (
          <Catalog
            key={user.githubId}
            apps={apps}
            loading={appsLoading}
            error={appsError}
            pendingCount={pendingCount}
            userId={user.githubId}
            onOpenApp={repo => navigate('detail', repo)}
            onCreate={() => navigate('templates')}
            onRetry={refreshApps}
          />
        )}

        <VibeCheckSession active={route.screen === 'vibe-check'} onBack={() => navigate('templates')} onUse={proposal => {
          setVibeProposal(proposal);
          navigate('wizard', 'cdp-primary-app');
        }} />

        {route.screen === 'wizard' && (
          <CreateWizard
            key={`${route.templateId ?? 'cdp-primary-app'}:${wizardRevision}:${vibeProposal ? JSON.stringify(vibeProposal) : 'blank'}`}
            onVibeCheck={() => navigate('vibe-check')}
            onRestart={() => navigate('templates')}
            initialProposal={vibeProposal ?? undefined}
            templateId={route.templateId ?? 'cdp-primary-app'}
            onBack={() => navigate('catalog')}
            onCreated={repo => {
              setVibeProposal(null);
              refreshApps();
              navigate('detail', repo);
            }}
          />
        )}

        {mountedDetailRepos.map(repo => {
          const visible = repo === visibleDetailRepo;
          return (
            <div key={repo} style={{ display: visible ? 'block' : 'none' }}>
              <AppDetail
                repo={repo}
                visible={visible}
                selectedTab={visible ? route.detailTab ?? 'Overview' : undefined}
                currentUserLogin={user.login}
                isCdpAdmin={user.isCdpAdmin}
                onBack={() => navigate('catalog')}
                onArchived={() => {
                  refreshApps();
                  setLiveSessionStates(states => {
                    const next = { ...states };
                    delete next[repo];
                    return next;
                  });
                  navigate('catalog');
                }}
                onChanged={refreshApps}
                onTabChange={tab => navigateDetailTab(repo, tab)}
                onLiveSessionStateChange={state => {
                  setLiveSessionStates(states => {
                    if (RETAINED_LIVE_SESSION_STATES.has(state)) {
                      return states[repo] === state ? states : { ...states, [repo]: state };
                    }
                    if (!(repo in states)) return states;
                    const next = { ...states };
                    delete next[repo];
                    return next;
                  });
                }}
                fromList={apps.find(app => app.repo === repo) ?? null}
              />
            </div>
          );
        })}

        {(route.screen === 'docs' || route.screen === 'settings') && (
          <Placeholder screen={route.screen} />
        )}

        {route.screen === 'feedback' && <Feedback />}
      </main>
    </div>
  );
}
